// SPDX-License-Identifier: GPL-3.0-or-later
// Routes engine events to the HUD, the live region and the renderer.
//
// Text channels (src/table/callouts.ts):
// - animate on target "Callout": the text is `calloutText(animation)`. It goes to the live region
//   only (no visible text in play).
// - animate on target "Hud": `timer:<s>:<total>` or `goal:<key>[:<n>]` (decision D5).
// - the engine event ball_saved has no rule call: show CALLOUT_LINES.ball_saved.
// Other animate events go to the renderer (the characters). So do light events.

import type { EngineEvent, TableRenderer } from "../src/shared/contracts.ts";
import { CALLOUT_LINES, calloutText, formatPoints, hudText } from "../src/table/callouts.ts";
import { CALLOUT_TARGET, HUD_TARGET } from "../src/table/ids.ts";
import { modeLabel } from "../src/table/modes.ts";
import type { Hud, LiveFeed, LivePriority } from "./hud.ts";

/** What the router needs. */
export interface EventTargets {
  readonly hud: Hud;
  readonly feed: LiveFeed;
  readonly renderer: Pick<TableRenderer, "applyEvent"> | null;
  readonly now: () => number;
}

/** One line for the live region. */
interface FeedLine {
  readonly text: string;
  readonly priority: LivePriority;
}

const high = (text: string): FeedLine => ({ text, priority: "high" });

/**
 * Say a callout in the live region only. Play is calm: no callout text shows on the screen while a
 * ball is in play. A screen reader still hears it.
 */
function callout(t: EventTargets, text: string): void {
  t.feed.push(text, "high");
}

type AnimateEvent = Extract<EngineEvent, { type: "animate" }>;

/** An animate event goes to the live region, to the HUD or to the characters. */
function routeAnimate(ev: AnimateEvent, t: EventTargets): void {
  if (ev.decoration === CALLOUT_TARGET) {
    const text = calloutText(ev.animation);
    if (text !== null) callout(t, text);
    return;
  }
  if (ev.decoration !== HUD_TARGET) {
    t.renderer?.applyEvent(ev);
    return;
  }
  const update = hudText(ev.animation);
  if (update === null) return;
  if (update.kind === "timer") t.hud.setTimer(update.secondsLeft, update.secondsTotal);
  else t.hud.setGoal(update.text);
}

/** The live region line of an event of the ball and game flow, or null. */
function flowLine(ev: EngineEvent): FeedLine | null {
  switch (ev.type) {
    case "game_start":
      return high(`Game started. ${ev.ballsPerGame} balls.`);
    case "ball_start":
      return high(`Ball ${ev.ballNumber}.`);
    case "ball_lost":
      return high(
        `Ball ${ev.ballNumber} ended. Bonus ${formatPoints(ev.bonus)}. ${ev.ballsLeft} balls left.`,
      );
    case "game_over":
      return high(`Game over. Final score ${formatPoints(ev.finalScore)}.`);
    default:
      return null;
  }
}

/** The live region line of a mode or score event, or null. */
function scoreLine(ev: EngineEvent): FeedLine | null {
  switch (ev.type) {
    case "mode_start":
      return high(`Mode started: ${ev.label}.`);
    case "mode_end":
      return high(`Mode ${ev.completed ? "complete" : "ended"}: ${modeLabel(ev.modeId)}.`);
    case "multiball":
      return ev.active ? high(`Multiball. ${ev.ballsInPlay} balls in play.`) : null;
    case "score":
      return { text: `Score ${formatPoints(ev.total)}.`, priority: "low" };
    default:
      return null;
  }
}

/** True for the events that end the timer and the goal line of the HUD. */
function clearsHud(ev: EngineEvent): boolean {
  return ev.type === "game_start" || ev.type === "mode_end" || ev.type === "game_over";
}

/** Handle one engine event. Unknown or empty text does nothing. */
export function routeEngineEvent(ev: EngineEvent, t: EventTargets): void {
  if (ev.type === "animate") {
    routeAnimate(ev, t);
    return;
  }
  if (ev.type === "light") {
    t.renderer?.applyEvent(ev);
    return;
  }
  if (ev.type === "ball_saved") {
    callout(t, CALLOUT_LINES.ball_saved);
    return;
  }
  if (clearsHud(ev)) {
    t.hud.clearTimer();
    t.hud.setGoal("");
  }
  const line = flowLine(ev) ?? scoreLine(ev);
  if (line !== null) t.feed.push(line.text, line.priority);
}
